import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Replayer } from 'rrweb';
import 'rrweb/dist/replay/rrweb-replay.min.css';
import type { eventWithTime } from '@rrweb/types';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000');

interface LiveSessionModalProps {
  applicationId: string;
  userEmail?: string;
  onClose: () => void;
}

const LiveSessionModal: React.FC<LiveSessionModalProps> = ({ applicationId, userEmail, onClose }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [currentPage, setCurrentPage] = useState('');
  const [eventCount, setEventCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');

  const socketRef = useRef<Socket | null>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const replayerRef = useRef<Replayer | null>(null);
  const eventsReceivedRef = useRef(0);
  const hasInitializedRef = useRef(false);
  const eventQueueRef = useRef<eventWithTime[]>([]);
  const addEventErrorCount = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scaleTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const scaleReplayerToFit = useCallback(() => {
    if (!playerContainerRef.current) return;
    const wrapper = playerContainerRef.current.querySelector('.replayer-wrapper') as HTMLElement;
    if (!wrapper) return;

    const container = playerContainerRef.current;
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;

    const iframe = wrapper.querySelector('iframe');
    const origW = iframe?.offsetWidth || wrapper.scrollWidth || 1920;
    const origH = iframe?.offsetHeight || wrapper.scrollHeight || 1080;

    const scaleX = containerW / origW;
    const scaleY = containerH / origH;
    const scale = Math.min(scaleX, scaleY, 1);

    wrapper.style.transformOrigin = 'top left';
    wrapper.style.transform = `scale(${scale})`;
    wrapper.style.width = `${origW}px`;
    wrapper.style.height = `${origH}px`;

    const scaledW = origW * scale;
    const offsetX = Math.max(0, (containerW - scaledW) / 2);
    wrapper.style.marginLeft = `${offsetX}px`;
  }, []);

  // Destroy the current replayer and clean up
  const destroyReplayer = useCallback(() => {
    if (replayerRef.current) {
      try { replayerRef.current.pause(); } catch (e) { /* ignore */ }
      try { replayerRef.current.destroy(); } catch (e) { /* ignore */ }
      replayerRef.current = null;
    }
    if (playerContainerRef.current) {
      if ((playerContainerRef.current as any).__cleanupResize) {
        (playerContainerRef.current as any).__cleanupResize();
      }
      playerContainerRef.current.innerHTML = '';
    }
    hasInitializedRef.current = false;
    eventQueueRef.current = [];
    addEventErrorCount.current = 0;
  }, []);

  // Initialize (or reinitialize) the replayer with a batch of events containing a full snapshot
  const initReplayer = useCallback((initialEvents: eventWithTime[]) => {
    if (!playerContainerRef.current) return;

    // Always destroy existing replayer first
    destroyReplayer();
    hasInitializedRef.current = true;

    try {
      const replayer = new Replayer(initialEvents, {
        root: playerContainerRef.current,
        liveMode: true,
        showWarning: false,
        showDebug: false,
        speed: 1,
        mouseTail: {
          strokeStyle: '#22c55e',
          lineWidth: 3,
          duration: 600,
        },
        UNSAFE_replayCanvas: true, // replay canvas elements (charts, signatures, etc.)
        useVirtualDom: false, // direct DOM manipulation for better modal/overlay rendering
        insertStyleRules: [
          `.replayer-mouse { width: 24px; height: 24px; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M4 1L4 18L8.5 13.5L13 21L15.5 19.5L11 12L17 12Z' fill='%23000' stroke='%23fff' stroke-width='1.5'/%3E%3C/svg%3E"); background-size: contain; background-repeat: no-repeat; border: none !important; border-radius: 0 !important; }`,
          `.replayer-mouse::after { content: ''; display: none; }`,
          `.replayer-mouse-tail { stroke-linecap: round; }`,
          // Ensure modals/overlays with fixed positioning render correctly inside the replay iframe
          `[style*="position: fixed"] { position: absolute !important; }`,
          `.MuiModal-root, .MuiDialog-root, .MuiPopover-root, .MuiMenu-root { position: absolute !important; }`,
          `.MuiBackdrop-root { position: absolute !important; }`,
        ],
      });

      replayerRef.current = replayer;
      replayer.startLive();

      const iframe = playerContainerRef.current.querySelector('iframe');
      if (iframe) iframe.style.border = 'none';

      // Multiple scaling attempts to handle iframe render timing
      const scaleTimes = [100, 300, 600, 1200, 2500];
      scaleTimersRef.current.forEach(t => clearTimeout(t));
      scaleTimersRef.current = scaleTimes.map(ms =>
        setTimeout(() => scaleReplayerToFit(), ms)
      );

      const handleResize = () => scaleReplayerToFit();
      window.addEventListener('resize', handleResize);
      (playerContainerRef.current as any).__cleanupResize = () => window.removeEventListener('resize', handleResize);

      // Auto-retry: if replayer iframe body is empty after 4 seconds, request a fresh snapshot
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => {
        if (!playerContainerRef.current || !replayerRef.current) return;
        const iframe = playerContainerRef.current.querySelector('iframe');
        const iframeBody = iframe?.contentDocument?.body;
        const hasContent = iframeBody && iframeBody.innerHTML && iframeBody.innerHTML.length > 100;
        if (!hasContent && socketRef.current?.connected) {
          console.warn('[LiveSession] Replayer appears blank after init, requesting fresh snapshot...');
          socketRef.current.emit('admin-request-snapshot', { applicationId });
        }
      }, 4000);

      console.log('[LiveSession] Replayer (re)initialized with', initialEvents.length, 'events');
    } catch (err) {
      console.error('[LiveSession] Replayer init error:', err);
      hasInitializedRef.current = false;
      setError('Failed to initialize live viewer');
    }
  }, [scaleReplayerToFit, destroyReplayer, applicationId]);

  useEffect(() => {
    const sock = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });
    socketRef.current = sock;

    sock.on('connect', () => {
      setIsConnected(true);
      setConnectionStatus('connected');
      sock.emit('admin-join-live-watchers');
      sock.emit('admin-watch-live', { applicationId });
    });

    sock.on('disconnect', () => {
      setIsConnected(false);
      setConnectionStatus('disconnected');
    });

    sock.on('live-session-status', (data: { applicationId: string; isLive: boolean; currentPage: string }) => {
      setIsLive(data.isLive);
      setCurrentPage(data.currentPage || '');
      if (!data.isLive) {
        setError('User is not currently online. Waiting for them to come live...');
      } else {
        // User is live — if we don't receive a snapshot within 3 seconds, request one
        setTimeout(() => {
          if (!hasInitializedRef.current && sock.connected) {
            console.log('[LiveSession] No snapshot received after status=live, requesting fresh snapshot...');
            sock.emit('admin-request-snapshot', { applicationId });
          }
        }, 3000);
      }
    });

    sock.on('live-session-data', (data: { applicationId: string; events: eventWithTime[] }) => {
      if (data.applicationId !== applicationId || !data.events || data.events.length === 0) return;

      setIsLive(true);
      setError(null);
      eventsReceivedRef.current += data.events.length;
      setEventCount(eventsReceivedRef.current);

      // Check if this batch contains a new full snapshot (type 2) — means the user's DOM changed entirely
      const hasFullSnapshot = data.events.some(evt => evt.type === 2);

      if (hasFullSnapshot) {
        // New full snapshot detected — reinitialize the replayer from this snapshot onward
        // Collect the snapshot and all events after it
        const snapshotIdx = data.events.findIndex(evt => evt.type === 2);
        const eventsFromSnapshot = data.events.slice(snapshotIdx);
        // Include meta event (type 4) if it precedes the snapshot in this batch
        const metaEvents = data.events.slice(0, snapshotIdx).filter(evt => evt.type === 4);
        const initBatch = [...metaEvents, ...eventsFromSnapshot];
        console.log('[LiveSession] New full snapshot detected, reinitializing replayer with', initBatch.length, 'events');
        initReplayer(initBatch);
        return;
      }

      if (!hasInitializedRef.current) {
        // Queue events until we get a full snapshot
        eventQueueRef.current.push(...data.events);
        // Check if the queue now has a full snapshot
        const queueHasSnapshot = eventQueueRef.current.some(evt => evt.type === 2);
        if (queueHasSnapshot) {
          const qSnapshotIdx = eventQueueRef.current.findIndex(evt => evt.type === 2);
          const initBatch = eventQueueRef.current.splice(qSnapshotIdx);
          eventQueueRef.current = []; // discard events before snapshot
          initReplayer(initBatch);
        }
      } else if (replayerRef.current) {
        let errorOccurred = false;
        for (const event of data.events) {
          try {
            replayerRef.current.addEvent(event);
          } catch (e) {
            addEventErrorCount.current++;
            errorOccurred = true;
          }
        }
        // If too many consecutive errors, the DOM is out of sync — reset and request fresh snapshot
        if (errorOccurred && addEventErrorCount.current > 5) {
          console.warn('[LiveSession] Too many addEvent errors, resetting replayer. Requesting fresh snapshot...');
          destroyReplayer();
          // Request a fresh snapshot from user's browser
          sock.emit('admin-request-snapshot', { applicationId });
        } else if (!errorOccurred) {
          addEventErrorCount.current = 0; // reset error counter on clean batch
        }
        requestAnimationFrame(() => scaleReplayerToFit());
      }
    });

    sock.on('live-session-page-change', (data: { applicationId: string; currentPage: string }) => {
      if (data.applicationId === applicationId) {
        setCurrentPage(data.currentPage);
      }
    });

    sock.on('live-session-ended', (data: { applicationId: string }) => {
      if (data.applicationId === applicationId) {
        setIsLive(false);
        setError('User session has ended. Will reconnect automatically if they come back online.');
        // Reset replayer so a new session can start fresh
        destroyReplayer();
      }
    });

    // Handle live-sessions-updated: user may come back online with same applicationId
    sock.on('live-sessions-updated', (data: { applicationId: string; status: string }) => {
      if (data.applicationId === applicationId && data.status === 'online') {
        setIsLive(true);
        setError(null);
        // Re-subscribe to get the new cached snapshot
        sock.emit('admin-watch-live', { applicationId });
      }
    });

    return () => {
      sock.emit('admin-stop-watching', { applicationId });
      sock.disconnect();
      socketRef.current = null;

      destroyReplayer();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      scaleTimersRef.current.forEach(t => clearTimeout(t));
    };
  }, [applicationId, initReplayer, scaleReplayerToFit, destroyReplayer]);

  const handleFullscreen = useCallback(() => {
    if (!modalRef.current) return;
    if (!document.fullscreenElement) {
      modalRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => scaleReplayerToFit(), 100);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [scaleReplayerToFit]);

  const overlayStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 99999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)',
  };

  const modalStyle: React.CSSProperties = {
    background: '#0f172a', borderRadius: isFullscreen ? '0' : '16px',
    width: isFullscreen ? '100vw' : '95vw', maxWidth: isFullscreen ? '100vw' : '1200px',
    height: isFullscreen ? '100vh' : 'auto', maxHeight: isFullscreen ? '100vh' : '92vh',
    overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
    display: 'flex', flexDirection: 'column',
    border: isFullscreen ? 'none' : '1px solid rgba(34,197,94,0.3)',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div ref={modalRef} style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(0,0,0,0.3)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Live badge */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '4px 12px', borderRadius: '20px',
              background: isLive ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
              border: `1px solid ${isLive ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
            }}>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: isLive ? '#22c55e' : '#ef4444',
                boxShadow: isLive ? '0 0 8px #22c55e, 0 0 16px rgba(34,197,94,0.4)' : 'none',
                animation: isLive ? 'livePulse 1.5s ease-in-out infinite' : 'none',
              }} />
              <span style={{
                fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.5px',
                color: isLive ? '#22c55e' : '#ef4444',
                textTransform: 'uppercase',
              }}>
                {isLive ? 'Live' : 'Offline'}
              </span>
            </div>

            <div>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                Live Session Viewer
              </h3>
              <div style={{ display: 'flex', gap: '12px', marginTop: '2px', fontSize: '0.7rem', color: '#94a3b8' }}>
                <span style={{ fontFamily: 'monospace' }}>{applicationId}</span>
                {userEmail && <span>{userEmail}</span>}
                {currentPage && <span style={{ color: '#818cf8' }}>Page: {currentPage}</span>}
                <span>{eventCount} events</span>
                <span style={{
                  color: connectionStatus === 'connected' ? '#22c55e' : connectionStatus === 'connecting' ? '#f59e0b' : '#ef4444',
                }}>
                  {connectionStatus === 'connected' ? '● Connected' : connectionStatus === 'connecting' ? '◌ Connecting...' : '● Disconnected'}
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* Refresh button — request fresh snapshot */}
            <button
              onClick={() => {
                if (socketRef.current?.connected) {
                  socketRef.current.emit('admin-request-snapshot', { applicationId });
                  console.log('[LiveSession] Manual refresh requested');
                }
              }}
              title="Refresh live view"
              style={{
                width: '34px', height: '34px', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                background: 'rgba(255,255,255,0.05)', color: '#94a3b8', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#94a3b8'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
            </button>
            {/* Fullscreen button */}
            <button
              onClick={handleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              style={{
                width: '34px', height: '34px', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                background: 'rgba(255,255,255,0.05)', color: '#94a3b8', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#94a3b8'; }}
            >
              {isFullscreen ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" />
                  <line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              )}
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              style={{
                width: '34px', height: '34px', border: 'none', borderRadius: '8px',
                background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.4)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            >&times;</button>
          </div>
        </div>

        {/* Player Area */}
        <div style={{
          flex: 1, overflow: 'hidden', position: 'relative',
          background: '#0a0a1a',
          minHeight: isFullscreen ? 'calc(100vh - 80px)' : '500px',
          maxHeight: isFullscreen ? 'calc(100vh - 80px)' : 'calc(92vh - 80px)',
        }}>
          {/* Connection/waiting overlay */}
          {(!isLive || !hasInitializedRef.current) && !error && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 5, background: 'rgba(10,10,26,0.9)',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '48px', height: '48px', border: '3px solid rgba(34,197,94,0.2)',
                  borderTop: '3px solid #22c55e', borderRadius: '50%',
                  margin: '0 auto 16px', animation: 'spin 1s linear infinite',
                }} />
                <div style={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 500 }}>
                  {connectionStatus === 'connecting' ? 'Connecting to live session...' : 'Waiting for live events...'}
                </div>
                <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '6px' }}>
                  The user must be actively using their dashboard
                </div>
              </div>
            </div>
          )}

          {/* Error overlay */}
          {error && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 5, background: 'rgba(10,10,26,0.9)',
            }}>
              <div style={{ textAlign: 'center', maxWidth: '400px' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5" style={{ margin: '0 auto 12px', display: 'block' }}>
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <div style={{ color: '#f59e0b', fontSize: '0.9rem', fontWeight: 500 }}>{error}</div>
                <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '6px' }}>
                  The modal will automatically connect when the user comes online
                </div>
              </div>
            </div>
          )}

          {/* rrweb replay container */}
          <div
            ref={playerContainerRef}
            style={{
              width: '100%', height: '100%',
              overflow: 'hidden',
            }}
          />

          {/* Bottom live indicator bar */}
          {isLive && hasInitializedRef.current && (
            <div style={{
              position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '6px 16px', borderRadius: '20px',
              background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(34,197,94,0.3)',
              zIndex: 6,
            }}>
              <div style={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: '#22c55e',
                animation: 'livePulse 1.5s ease-in-out infinite',
              }} />
              <span style={{ color: '#22c55e', fontSize: '0.72rem', fontWeight: 600 }}>LIVE</span>
              <span style={{ color: '#64748b', fontSize: '0.68rem' }}>|</span>
              <span style={{ color: '#94a3b8', fontSize: '0.68rem' }}>{eventCount} events</span>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes livePulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 4px #22c55e; }
          50% { opacity: 0.5; box-shadow: 0 0 12px #22c55e, 0 0 24px rgba(34,197,94,0.3); }
        }
        .replayer-wrapper { transform-origin: top left; }
      `}</style>
    </div>
  );
};

export default LiveSessionModal;
